import React, { useState } from 'react';
import type {
  LcDirectory,
  LcNapSource,
  LcScanInfo,
  LcStatusKey,
  LcStats,
  LcTierDef,
  LcToneKey,
} from '../../growth-citations/types';
import {
  LC_ACTIONS,
  LC_NAP,
  LC_STATS,
  LC_STATUS_LABELS,
  LC_TIERS,
} from '../../growth-citations/mockData';
import { GROWTH_CITATIONS_META } from '../../growth-citations/config';
import { Icon } from '../Icon/Icon';
import './growth-citations.css';

export const LC_TONE: Record<LcToneKey, { accent: string; tint: string }> = {
  ai: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: { accent: 'var(--terracotta)', tint: 'color-mix(in srgb, var(--terracotta) 10%, transparent)' },
  lav: { accent: '#7A5CFF', tint: 'rgba(122,79,214,0.13)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

export function LcCard({
  children,
  className = '',
  padding = true,
}: {
  children: React.ReactNode;
  className?: string;
  padding?: boolean;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        padding ? 'p-[22px]' : '',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

export function LcPill({ status }: { status: LcStatusKey }) {
  const st = LC_STATUS_LABELS[status] ?? { label: status, tone: 'neutral' as LcToneKey };
  const tone = LC_TONE[st.tone] ?? LC_TONE.neutral;
  return (
    <span
      className="inline-flex items-center gap-[5px] whitespace-nowrap rounded-[var(--r-pill)] px-2.5 py-[3px] text-[11px] font-bold"
      style={{ background: tone.tint, color: tone.accent }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone.accent }} />
      {st.label}
    </span>
  );
}

export function LcGauge({
  stats = LC_STATS,
  scanInfo,
}: {
  stats?: LcStats;
  scanInfo?: LcScanInfo;
}) {
  const s = stats;
  const pct = s.total > 0 ? Math.round((s.consistent / s.total) * 100) : 0;
  const toneKey: LcToneKey = pct >= 90 ? 'success' : pct >= 70 ? 'gold' : 'terra';
  const tone = LC_TONE[toneKey];
  const r = 52;
  const circ = 2 * Math.PI * r;

  return (
    <LcCard className="flex flex-wrap items-center gap-[26px]">
      <div className="relative h-[124px] w-[124px] shrink-0">
        <svg width="124" height="124" className="-rotate-90">
          <circle cx="62" cy="62" r={r} fill="none" stroke="var(--surface-mute)" strokeWidth="10" />
          <circle
            cx="62"
            cy="62"
            r={r}
            fill="none"
            stroke={tone.accent}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={circ * (1 - pct / 100)}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.04em] text-[var(--ink-0)]">
            {pct}%
          </span>
          <span className={`${eyebrowClass} mt-[3px]`}>Consistent</span>
        </div>
      </div>
      <div className="min-w-[280px] flex-1">
        <div className="mb-[5px] font-[var(--font-display)] text-xl font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          Citation consistency score
        </div>
        <p className="mb-4 text-[13px] leading-[1.6] text-[var(--ink-2)]">
          Your NAP matches on{' '}
          <b className="font-semibold">
            {s.consistent} of {s.total}
          </b>{' '}
          directories. ELVA is actively correcting{' '}
          <b className="font-semibold text-[var(--gold)]">{s.inconsistent}</b> inconsistencies and has
          flagged{' '}
          <b className="font-semibold text-[var(--terracotta)]">
            {s.duplicate}
          </b>{' '}
          duplicate listing{s.duplicate === 1 ? '' : 's'} that need your attention.
        </p>
        <div className="flex flex-wrap gap-[22px]">
          <div>
            <div className={`${eyebrowClass} mb-1`}>Fixes this month</div>
            <div className="flex items-baseline gap-[7px]">
              <span className="font-[var(--font-display)] text-xl font-bold text-[var(--success)]">
                {s.recentFixes}
              </span>
              <span className="text-xs text-[var(--ink-3)]">auto-corrections pushed by ELVA</span>
            </div>
          </div>
          <div className="w-px bg-[var(--cream-2)]" />
          <div>
            <div className={`${eyebrowClass} mb-1`}>Last scan</div>
            <div className="text-[13px] text-[var(--ink-1)]">
              {scanInfo?.lastScan ?? '—'}
              {scanInfo?.nextScan ? (
                <>
                  {' '}
                  · <span className="text-[var(--ink-3)]">next {scanInfo.nextScan}</span>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </LcCard>
  );
}

function NapField({
  label,
  value,
  mono,
  link,
  span,
}: {
  label: string;
  value: string;
  mono?: boolean;
  link?: boolean;
  span?: boolean;
}) {
  return (
    <div className={span ? 'col-span-full' : undefined}>
      <div className={`${eyebrowClass} mb-1`}>{label}</div>
      <div
        className={[
          'text-[13.5px] font-medium',
          link ? 'text-[var(--info)]' : 'text-[var(--ink-0)]',
          mono ? 'font-[var(--font-mono)]' : 'font-[var(--font-body)]',
        ].join(' ')}
      >
        {value}
      </div>
    </div>
  );
}

export function LcNapSource({ nap }: { nap?: LcNapSource }) {
  const n = nap ?? LC_NAP;
  return (
    <div className="relative flex items-start gap-[18px] overflow-hidden rounded-[var(--r-xl)] border border-[color-mix(in_srgb,var(--ai-3)_26%,transparent)] bg-[linear-gradient(135deg,var(--ai-tint),var(--cream-1)_55%)] p-[24px_26px] shadow-[var(--sh-1)]">
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--cream-1)] text-[var(--ai-3)] shadow-[var(--sh-1)]">
        <Icon kind="shield-check" size={22} stroke={1.9} />
      </span>
      <div className="min-w-0 flex-1">
        <div className={`${eyebrowClass} mb-1.5 tracking-[0.1em] text-[var(--ai-3)]`}>
          Source of truth
        </div>
        <div className="mb-1 font-[var(--font-display)] text-[19px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
          ELVA uses this NAP across all directories
        </div>
        <p className="mb-[18px] text-[13px] leading-[1.55] text-[var(--ink-2)]">
          This is what ELVA pushes to every listing. To update any field, change it in your PMS — ELVA
          syncs automatically.
        </p>
        <div className="lc-nap-grid grid grid-cols-2 gap-x-6 gap-y-3.5 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-[18px_22px]">
          <NapField label="Business name" value={n.name} />
          <NapField label="Phone" value={n.phone} mono />
          <NapField label="Address" value={n.address} />
          <NapField label="Website" value={n.website} mono link />
          {n.hours ? <NapField label="Hours" value={n.hours} span /> : null}
        </div>
        {n.lastUpdated ? (
          <div className="mt-3 font-[var(--font-mono)] text-[11.5px] text-[var(--ink-3)]">
            Last synced {n.lastUpdated}
          </div>
        ) : (
          <div className="mt-3 font-[var(--font-mono)] text-[11.5px] text-[var(--ink-3)]">
            From clinic profile · hours not synced from API
          </div>
        )}
      </div>
    </div>
  );
}

function LcRow({
  directory,
  last,
  onCreateListing,
  onClaimListing,
  onHelpResolve,
  pendingDirectoryKey,
}: {
  directory: LcDirectory;
  last: boolean;
  onCreateListing?: (directoryKey: string) => void;
  onClaimListing?: (directoryKey: string) => void;
  onHelpResolve?: (directoryKey: string) => void;
  pendingDirectoryKey?: string;
}) {
  const showIssue =
    directory.issue &&
    (['inconsistent', 'duplicate', 'unclaimed'] as LcStatusKey[]).includes(directory.status);
  const action = LC_ACTIONS[directory.status];
  const actionTone = action ? LC_TONE[action.tone] : null;
  const directoryKey = directory.directoryKey || directory.id;
  const isPending = pendingDirectoryKey === directoryKey;

  const handleAction = () => {
    if (isPending) return;
    if (directory.status === 'missing') onCreateListing?.(directoryKey);
    else if (directory.status === 'unclaimed') onClaimListing?.(directoryKey);
    else if (directory.status === 'duplicate') onHelpResolve?.(directoryKey);
  };

  return (
    <div
      className="flex items-center gap-3.5 px-5 py-[13px]"
      style={{ borderBottom: last ? undefined : '1px solid var(--cream-2)' }}
    >
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-[var(--surface-mute)] font-[var(--font-display)] text-[12.5px] font-bold text-[var(--ink-2)]">
        {directory.logo}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-[var(--ink-0)]">{directory.name}</div>
        {showIssue ? (
          <div className="mt-0.5 text-xs leading-[1.4] text-[var(--ink-2)]">{directory.issue}</div>
        ) : directory.status === 'missing' ? (
          <div className="mt-0.5 text-[11.5px] text-[var(--ink-4)]">Clinic not listed on this directory</div>
        ) : (
          <div className="mt-0.5 font-[var(--font-mono)] text-[11.5px] text-[var(--ink-4)]">
            Last checked {directory.lastChecked}
          </div>
        )}
      </div>
      <LcPill status={directory.status} />
      {action && actionTone ? (
        <button
          type="button"
          disabled={isPending}
          onClick={handleAction}
          className="lc-act shrink-0 cursor-pointer whitespace-nowrap rounded-[var(--r-sm)] border px-3.5 py-[7px] text-xs font-semibold font-[var(--font-body)] disabled:cursor-wait disabled:opacity-60"
          style={{
            borderColor: `color-mix(in srgb, ${actionTone.accent} 40%, transparent)`,
            background: actionTone.tint,
            color: actionTone.accent,
          }}
        >
          {isPending ? 'Working…' : action.label}
        </button>
      ) : (
        directory.listingUrl ? (
          <a
            href={directory.listingUrl}
            target="_blank"
            rel="noreferrer"
            title="View listing"
            className="lc-ext inline-flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-[var(--r-sm)] border-0 bg-transparent text-[var(--ink-4)]"
          >
            <Icon kind="external" size={14} stroke={1.9} />
          </a>
        ) : (
          <button
            type="button"
            title="View listing"
            className="lc-ext inline-flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-[var(--r-sm)] border-0 bg-transparent text-[var(--ink-4)]"
          >
            <Icon kind="external" size={14} stroke={1.9} />
          </button>
        )
      )}
    </div>
  );
}

function LcTier({
  tier,
  onCreateListing,
  onClaimListing,
  onHelpResolve,
  pendingDirectoryKey,
}: {
  tier: LcTierDef;
  onCreateListing?: (directoryKey: string) => void;
  onClaimListing?: (directoryKey: string) => void;
  onHelpResolve?: (directoryKey: string) => void;
  pendingDirectoryKey?: string;
}) {
  const [open, setOpen] = useState(Boolean(tier.defaultOpen));
  const tone = LC_TONE[tier.tone];
  const consistent = tier.directories.filter((d) => d.status === 'consistent').length;
  const issues = tier.directories.length - consistent;

  return (
    <LcCard className="mb-3.5 overflow-hidden !p-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full cursor-pointer items-center gap-3.5 border-0 bg-transparent px-5 py-4 text-left"
        style={{ borderBottom: open ? '1px solid var(--cream-2)' : undefined }}
      >
        <span
          className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] font-[var(--font-display)] text-lg font-extrabold"
          style={{ background: tone.tint, color: tone.accent }}
        >
          {tier.tier}
        </span>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-2">
            <span className="font-[var(--font-display)] text-base font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
              {tier.title}
            </span>
            {tier.badge ? (
              <span className="rounded-[var(--r-pill)] bg-[var(--ai-tint)] px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.04em] text-[var(--ai-3)]">
                {tier.badge}
              </span>
            ) : null}
          </div>
          <div className="text-xs text-[var(--ink-3)]">{tier.subtitle}</div>
        </div>
        <div className="shrink-0 text-right">
          <div
            className="text-[12.5px] font-semibold"
            style={{ color: issues > 0 ? 'var(--gold)' : 'var(--success)' }}
          >
            {consistent} / {tier.directories.length} consistent
          </div>
          {issues > 0 ? (
            <div className="mt-0.5 text-[11px] text-[var(--ink-3)]">
              {issues} need{issues === 1 ? 's' : ''} attention
            </div>
          ) : null}
        </div>
        <Icon
          kind="down"
          size={16}
          stroke={2.2}
          className="shrink-0 text-[var(--ink-4)] transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : undefined }}
        />
      </button>
      {open ? (
        <div className="elva-fade">
          {tier.directories.map((directory, index) => (
            <LcRow
              key={directory.id}
              directory={directory}
              last={index === tier.directories.length - 1}
              {...(onCreateListing ? { onCreateListing } : {})}
              {...(onClaimListing ? { onClaimListing } : {})}
              {...(onHelpResolve ? { onHelpResolve } : {})}
              {...(pendingDirectoryKey ? { pendingDirectoryKey } : {})}
            />
          ))}
        </div>
      ) : null}
    </LcCard>
  );
}

function LcTile({ status, value }: { status: LcStatusKey; value: number }) {
  const st = LC_STATUS_LABELS[status];
  const tone = LC_TONE[st.tone];
  return (
    <LcCard className="px-[17px] py-[15px]">
      <div className="mb-2 flex items-center gap-1.5">
        <span className="h-[7px] w-[7px] rounded-full" style={{ background: tone.accent }} />
        <span className={eyebrowClass}>{st.label}</span>
      </div>
      <div
        className="font-[var(--font-display)] text-[23px] font-extrabold leading-none tracking-[-0.04em]"
        style={{ color: tone.accent }}
      >
        {value}
      </div>
    </LcCard>
  );
}

export interface LocalCitationsViewProps {
  tiers?: LcTierDef[];
  stats?: LcStats;
  napSource?: LcNapSource;
  scanInfo?: LcScanInfo;
  loading?: boolean;
  onCreateListing?: (directoryKey: string) => void;
  onClaimListing?: (directoryKey: string) => void;
  onHelpResolve?: (directoryKey: string) => void;
  pendingDirectoryKey?: string;
}

export function LocalCitationsView({
  tiers: tiersProp,
  stats: statsProp,
  napSource,
  scanInfo,
  loading = false,
  onCreateListing,
  onClaimListing,
  onHelpResolve,
  pendingDirectoryKey,
}: LocalCitationsViewProps = {}) {
  const meta = GROWTH_CITATIONS_META;
  const tiers = tiersProp ?? LC_TIERS;
  const s = statsProp ?? LC_STATS;

  return (
    <div className="growth-citations-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="mb-2 flex items-center gap-2">
          <Icon kind="pin" size={13} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
          {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
        </h1>
        <p className="mt-2.5 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
          {meta.description.replace('directories', `${s.total}+ directories`)}
          {loading ? ' · Updating…' : ''}
        </p>
      </div>

      <div className="mb-3.5">
        <LcGauge stats={s} {...(scanInfo ? { scanInfo } : {})} />
      </div>

      <div
        className="lc-status-grid mb-7 grid gap-3"
        style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
      >
        <LcTile status="consistent" value={s.consistent} />
        <LcTile status="inconsistent" value={s.inconsistent} />
        <LcTile status="duplicate" value={s.duplicate} />
        <LcTile status="missing" value={s.missing} />
        <LcTile status="unclaimed" value={s.unclaimed} />
      </div>

      <div className="mb-[30px]">
        <LcNapSource {...(napSource ? { nap: napSource } : {})} />
      </div>

      <h2 className="mb-3.5 mt-0 font-[var(--font-display)] text-xl font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
        Directory listings
      </h2>
      {tiers.map((tier) => (
        <LcTier
          key={tier.tier}
          tier={tier}
          {...(onCreateListing ? { onCreateListing } : {})}
          {...(onClaimListing ? { onClaimListing } : {})}
          {...(onHelpResolve ? { onHelpResolve } : {})}
          {...(pendingDirectoryKey ? { pendingDirectoryKey } : {})}
        />
      ))}

      <div className="mt-[18px] flex items-start gap-3.5 rounded-[var(--r-xl)] border border-[color-mix(in_srgb,var(--ai-3)_22%,transparent)] bg-[var(--ai-tint)] p-[18px_22px]">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[var(--cream-1)] text-[var(--ai-3)]">
          <Icon kind="sparkle" size={17} stroke={1.9} />
        </span>
        <div className="flex-1 text-[13px] leading-[1.65] text-[var(--ink-2)]">
          <b className="text-[var(--ink-0)]">How ELVA manages citations:</b> ELVA scans all {s.total}{' '}
          directories every 6 hours. When a listing doesn&apos;t match your source-of-truth NAP, ELVA
          submits a correction through the directory&apos;s update system — no action needed from you.
          Missing listings show up as opportunities (click &quot;Create listing&quot; to approve).
          Duplicate listings need your help to resolve safely — ELVA won&apos;t auto-delete, to avoid
          removing legitimate listings by accident.
        </div>
      </div>
    </div>
  );
}
